import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {uuidZodValidator} from "@vesta/global-common-typescript/validators/zodValidators";
import {OttopilotAdditionalButtonFormFieldRole,
type OttopilotAdditionalButtonFormColumnData,
type OttopilotAdditionalButtonRuleCondition,
type OttopilotAdditionalButtonVisibilityRule,
type OttopilotAdditionalButtonsConfig,
OttopilotOperator,
OttopilotRunTimeValueKind} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventsWorkspaceCapability/domain/OttopilotEventAdditionalButtonsConfig";
import type {EventWithEventContainer} from "~/utilities/apis/api";

function isEmptyAttributeValue(value: unknown): boolean {
    if (value === null || value === undefined) {
        return true;
    }
    if (typeof value === "string" && value.trim() === "") {
        return true;
    }
    if (Array.isArray(value) && value.length === 0) {
        return true;
    }
    return false;
}

function attributeValueToComparableString(value: unknown): string {
    if (value === null || value === undefined) {
        return "";
    }
    if (typeof value === "string") {
        return value;
    }
    if (typeof value === "number" || typeof value === "boolean") {
        return String(value);
    }
    if (typeof value === "bigint") {
        return String(value);
    }
    if (typeof value === "object") {
        try {
            return JSON.stringify(value);
        } catch {
            return "";
        }
    }
    return "";
}

function coerceToEpochMs(value: unknown): number | null {
    if (typeof value === "number" && Number.isFinite(value)) {
        return value;
    }
    if (typeof value === "string") {
        const parsed = Date.parse(value);
        if (Number.isNaN(parsed) === false) {
            return parsed;
        }
    }
    return null;
}

function evaluateAdditionalButtonRuleCondition(
    condition: OttopilotAdditionalButtonRuleCondition,
    context: {
        getAttributeValue: (attribute: string) => unknown;
        userHasTemplateId: (templateId: Uuid) => boolean;
        nowEpochMs: number;
    },
): boolean {
    const leftRaw = context.getAttributeValue(condition.attribute);

    if (condition.kind === OttopilotRunTimeValueKind.userTemplate) {
        if (condition.operator === OttopilotOperator.equals) {
            return context.userHasTemplateId(condition.value);
        }
        if (condition.operator === OttopilotOperator.notEquals) {
            return context.userHasTemplateId(condition.value) === false;
        }
    }

    const leftStr = attributeValueToComparableString(leftRaw);

    if (condition.kind === OttopilotRunTimeValueKind.static) {
        const rightStr = condition.value;
        if (condition.operator === OttopilotOperator.equals) {
            return leftStr === rightStr;
        }
        if (condition.operator === OttopilotOperator.notEquals) {
            return leftStr !== rightStr;
        }
        if (condition.operator === OttopilotOperator.contains) {
            return leftStr.includes(rightStr);
        }
        if (condition.operator === OttopilotOperator.notContains) {
            return leftStr.includes(rightStr) === false;
        }
        if (condition.operator === OttopilotOperator.isEmpty) {
            return isEmptyAttributeValue(leftRaw);
        }
        if (condition.operator === OttopilotOperator.isNotEmpty) {
            return isEmptyAttributeValue(leftRaw) === false;
        }
        if (condition.operator === OttopilotOperator.isNull) {
            return leftRaw === null;
        }
        return leftRaw !== null;
    }

    if (condition.kind === OttopilotRunTimeValueKind.userTemplate) {
        const rightStr = String(condition.value);
        if (condition.operator === OttopilotOperator.contains) {
            return leftStr.includes(rightStr);
        }
        if (condition.operator === OttopilotOperator.notContains) {
            return leftStr.includes(rightStr) === false;
        }
        if (condition.operator === OttopilotOperator.isEmpty) {
            return isEmptyAttributeValue(leftRaw);
        }
        if (condition.operator === OttopilotOperator.isNotEmpty) {
            return isEmptyAttributeValue(leftRaw) === false;
        }
        if (condition.operator === OttopilotOperator.isNull) {
            return leftRaw === null;
        }
        return leftRaw !== null;
    }

    const leftMs = coerceToEpochMs(leftRaw);
    const rightMs = condition.value;
    if (condition.operator === OttopilotOperator.equals) {
        if (leftMs === null) {
            return false;
        }
        return leftMs === rightMs;
    }
    if (condition.operator === OttopilotOperator.notEquals) {
        if (leftMs === null) {
            return true;
        }
        return leftMs !== rightMs;
    }
    if (condition.operator === OttopilotOperator.isEmpty) {
        return leftMs === null;
    }
    if (condition.operator === OttopilotOperator.isNotEmpty) {
        return leftMs !== null;
    }
    if (condition.operator === OttopilotOperator.isNull) {
        return leftRaw === null;
    }
    return leftRaw !== null;
}

function evaluateAdditionalButtonVisibilityRule(
    rule: OttopilotAdditionalButtonVisibilityRule,
    context: {
        getAttributeValue: (attribute: string) => unknown;
        userHasTemplateId: (templateId: Uuid) => boolean;
        nowEpochMs: number;
    },
): boolean {
    if (rule.conditions.length === 0) {
        return true;
    }
    if (rule.logicalOperator === "and") {
        for (const condition of rule.conditions) {
            if (evaluateAdditionalButtonRuleCondition(condition, context) === false) {
                return false;
            }
        }
        return true;
    }
    for (const condition of rule.conditions) {
        if (evaluateAdditionalButtonRuleCondition(condition, context) === true) {
            return true;
        }
    }
    return false;
}

export type AdditionalButtonRulesEvaluationContext = {
    /** Resolves the left-hand side for `attribute` (event field path, journey field, etc.). */
    getAttributeValue: (attribute: string) => unknown;
    /**
     * For {@link OttopilotRunTimeValueKind.userTemplate} with {@link OttopilotOperator.equals} / {@link OttopilotOperator.notEquals},
     * whether the signed-in user has that user-template or role id. Other operators compare the attribute value to the id as strings.
     */
    userHasTemplateId: (templateId: Uuid) => boolean;
    /** Defaults to `Date.now()` when omitted. */
    nowEpochMs?: number;
    /**
     * How top-level rule groups in `rules` combine. `"or"` — any group passing allows the button; `"and"` — every group must pass.
     * Defaults to `"or"`.
     */
    combineTopLevelRulesWith?: "and" | "or";
};

/**
 * Whether an additional button should be rendered given its visibility rules and runtime context.
 * Empty `rules` means there is no restriction — returns `true`.
 */
export function getShouldRenderAdditionalButton(
    rules: Array<OttopilotAdditionalButtonVisibilityRule>,
    context: AdditionalButtonRulesEvaluationContext,
): boolean {
    if (rules.length === 0) {
        return true;
    }
    const nowEpochMs = context.nowEpochMs ?? Date.now();
    const innerContext = {
        getAttributeValue: context.getAttributeValue,
        userHasTemplateId: context.userHasTemplateId,
        nowEpochMs: nowEpochMs,
    };
    const combineTopLevelRulesWith = context.combineTopLevelRulesWith ?? "or";
    if (combineTopLevelRulesWith === "and") {
        for (const rule of rules) {
            if (evaluateAdditionalButtonVisibilityRule(rule, innerContext) === false) {
                return false;
            }
        }
        return true;
    }
    for (const rule of rules) {
        if (evaluateAdditionalButtonVisibilityRule(rule, innerContext) === true) {
            return true;
        }
    }
    return false;
}

export function getShouldRenderAdditionalButtonForEvent(params: {
    rules: Array<OttopilotAdditionalButtonVisibilityRule>;
    event: EventWithEventContainer;
    currentUserTemplateIds: ReadonlySet<Uuid>;
}): boolean {
    return getShouldRenderAdditionalButton(params.rules, {
        getAttributeValue: (attribute) => {
            if (attribute === "createdAt") {
                return params.event.createdAt;
            }
            if (attribute === "createdBy") {
                return params.event.createdBy;
            }
            if (attribute === "updatedAt") {
                return params.event.updatedAt;
            }
            if (attribute === "updatedBy") {
                return params.event.updatedBy;
            }
            const columnIdParseResult = uuidZodValidator.safeParse(attribute);
            if (columnIdParseResult.success !== true) {
                return undefined;
            }
            const columnId = columnIdParseResult.data;
            if (Object.hasOwn(params.event.additionalColumnValues, columnId)) {
                return params.event.additionalColumnValues[columnId];
            }
            if (Object.hasOwn(params.event.eventContainer.additionalColumnValues, columnId)) {
                return params.event.eventContainer.additionalColumnValues[columnId];
            }
            return undefined;
        },
        userHasTemplateId: (templateId) => {
            return params.currentUserTemplateIds.has(templateId);
        },
    });
}

/** Config entries on an event template that pass visibility rules for the given row. Preserves config array order. */
export function filterAdditionalButtonsVisibleForEvent(params: {
    additionalButtons: Array<OttopilotAdditionalButtonsConfig> | undefined;
    event: EventWithEventContainer;
    currentUserTemplateIds: ReadonlySet<Uuid>;
}): Array<OttopilotAdditionalButtonsConfig> {
    const list = params.additionalButtons ?? [];
    return list.filter((config) => {
        return getShouldRenderAdditionalButtonForEvent({
            rules: config.rules,
            event: params.event,
            currentUserTemplateIds: params.currentUserTemplateIds,
        });
    });
}

/** Ordered copy of `formColumnData` using {@link OttopilotAdditionalButtonFormColumnData.sortOrder} (default 0). */
export function getAdditionalButtonFormColumnsSorted(
    formColumnData: Array<OttopilotAdditionalButtonFormColumnData> | undefined,
): Array<OttopilotAdditionalButtonFormColumnData> {
    if (formColumnData === undefined) {
        return [];
    }
    return [...formColumnData].sort((a, b) => {
        return (a.sortOrder ?? 0) - (b.sortOrder ?? 0);
    });
}

/**
 * The first field marked {@link OttopilotAdditionalButtonFormFieldRole.cancelRemarks}, if any.
 * Configurations should define at most one such row.
 */
export function getAdditionalButtonCancelRemarksColumn(
    formColumnData: Array<OttopilotAdditionalButtonFormColumnData> | undefined,
): OttopilotAdditionalButtonFormColumnData | undefined {
    if (formColumnData === undefined) {
        return undefined;
    }
    return formColumnData.find((row) => {
        return row.fieldRole === OttopilotAdditionalButtonFormFieldRole.cancelRemarks;
    });
}
